import { cn } from '@core/utils';
import { throttle } from 'es-toolkit';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import type { HTMLTagProps } from '@/types';

interface Location {
  top: number;
  left: number;
}

enum OPENING_TRANSITION {
  OPENING = 1,
  OPENED = 2,
  CLOSING = 3,
  CLOSED = 0,
}

interface TooltipContextValue {
  locationState: [Location, ReturnType<typeof throttle>];
  tooltipState: [
    OPENING_TRANSITION,
    (status: boolean, runInstantly?: boolean) => void,
  ];
}

const TooltipContext = createContext<TooltipContextValue | undefined>(
  undefined,
);

export interface TooltipContextProviderProps {
  children: React.ReactNode;
  mouseEnterDelay?: number;
}

const locationReducer = (_: Location, event: Location) => ({
  left: event.left,
  top: event.top + 10,
});

const CLOSING_DURATION = 100;

const TooltipContextProvider = ({
  children,
  mouseEnterDelay = 200,
}: TooltipContextProviderProps) => {
  const [status, setStatus] = useState(OPENING_TRANSITION.CLOSED);

  useEffect(() => {
    switch (status) {
      case OPENING_TRANSITION.CLOSING: {
        const closeTimer = setTimeout(() => {
          setStatus(OPENING_TRANSITION.CLOSED);
        }, CLOSING_DURATION);

        return () => clearTimeout(closeTimer);
      }

      case OPENING_TRANSITION.OPENING: {
        const openTimer = setTimeout(
          () => setStatus(OPENING_TRANSITION.OPENED),
          mouseEnterDelay,
        );

        return () => clearTimeout(openTimer);
      }
    }
  }, [mouseEnterDelay, setStatus, status]);

  const [location, dispatchLocation] = useReducer(locationReducer, {
    left: 0,
    top: 0,
  });

  const [throttledDispatchLocation, handleStatus] = useMemo(
    () => [
      throttle(dispatchLocation, 100),
      (status: boolean, runInstantly?: boolean) => {
        if (status) {
          if (mouseEnterDelay && !runInstantly)
            setStatus(OPENING_TRANSITION.OPENING);
          else setStatus(OPENING_TRANSITION.OPENED);
        } else if (CLOSING_DURATION && !runInstantly)
          setStatus(OPENING_TRANSITION.CLOSING);
        else setStatus(OPENING_TRANSITION.CLOSED);
      },
    ],
    [mouseEnterDelay],
  );

  const tooltipContextValue = useMemo(
    () =>
      ({
        locationState: [location, throttledDispatchLocation] as const,
        tooltipState: [status, handleStatus] as const,
      }) satisfies TooltipContextValue,
    [handleStatus, location, status, throttledDispatchLocation],
  );

  return (
    <TooltipContext.Provider value={tooltipContextValue}>
      <>{children}</>
    </TooltipContext.Provider>
  );
};

const useTooltipContext = () => {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error(
      'useTooltipContext must be used within a TooltipContextProvider',
    );
  }

  return context;
};

type TooltipAreaProps = HTMLTagProps<'div'>;

const TooltipArea = ({
  //* HTML div props
  className,
  onMouseMove,
  onMouseLeave,
  ...restDivProps
}: TooltipAreaProps) => {
  const {
    tooltipState: [, setOpeningTransition],
    locationState: [, dispatchLocation],
  } = useTooltipContext();

  useEffect(() => {
    const scrollEventHandler = () => setOpeningTransition(false, true);
    document.addEventListener('scroll', scrollEventHandler);

    return () => document.removeEventListener('scroll', scrollEventHandler);
  }, [setOpeningTransition]);

  return (
    <div
      {...restDivProps}
      className={cn('w-fit cursor-help', className)}
      onMouseLeave={(e) => {
        setOpeningTransition(false);
        onMouseLeave?.(e);
        dispatchLocation({
          left: e.clientX,
          top: e.clientY,
        });
      }}
      onMouseMove={(e) => {
        setOpeningTransition(true);
        dispatchLocation({
          left: e.clientX,
          top: e.clientY,
        });
        onMouseMove?.(e);
      }}
    />
  );
};

interface TooltipContentProps extends Omit<HTMLTagProps<'div'>, 'styles'> {
  align?: 'start' | 'center' | 'end';
}

const TooltipContent = ({
  //* HTML div props
  children,
  className,
  onMouseEnter,
  onMouseLeave,
  align = 'center',
  style,
  ...restDivProps
}: TooltipContentProps) => {
  const {
    tooltipState: [openingTransition, setOpeningTransition],
    locationState: [location],
  } = useTooltipContext();

  return openingTransition
    ? createPortal(
        <div
          {...restDivProps}
          className={cn(
            'animate-fade-in fixed z-10 flex flex-col transition-[left,top] duration-300 animate-duration-[100ms]',
            align === 'center' && 'items-center',
            align === 'start' && 'items-start',
            align === 'end' && 'items-end',
            openingTransition === OPENING_TRANSITION.CLOSING &&
              'animate-fade-out animate-duration-[100ms]',
          )}
          style={{
            left: location.left,
            top: location.top,
          }}
          onMouseEnter={(e) => {
            setOpeningTransition(true);
            onMouseEnter?.(e);
          }}
          onMouseLeave={(e) => {
            setOpeningTransition(false);
            onMouseLeave?.(e);
          }}
        >
          <div className="absolute z-[1] size-0 border-x-[7px] border-b-8 border-solid border-transparent border-b-[rgba(74,77,85,0.8)]" />
          <div
            className={cn(
              'absolute z-[2] mt-8 w-fit whitespace-pre rounded-xsmall bg-[rgba(74,77,85,0.8)] p-12 px-16 text-[0.875rem] text-Gray-white',
              align === 'start' && '-left-8',
              align === 'end' && '-right-8',
              className,
            )}
            style={style}
          >
            {children}
          </div>
        </div>,
        document.body,
      )
    : null;
};

export const Tooltip = Object.assign(TooltipContextProvider, {
  Area: TooltipArea,
  Content: TooltipContent,
});
